'use client';

import React from 'react';
import styled from 'styled-components';
import { Accordion } from '../Accordion/Accordion';

/**
 * AlertHistory Component Props
 */
export interface AlertHistoryProps {
  /** Array of alert objects to display */
  alerts: Alert[];
  /** Optional title for the history section */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Optional action buttons */
  headerActions?: React.ReactNode;
  /** Optional CSS class name */
  className?: string;
}

export interface Alert {
  id: string;
  name: string;
  triggeredAt: string;
  pattern: string;
  matchDetails: string;
  emailSent: boolean;
  logEntry: {
    timestamp: string;
    endpoint: string;
    dataLength: number;
  };
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HistoryHeader = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
`;

const HistoryTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const HistorySubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 8px 0 0 0;
`;

const HistoryHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const AlertList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ListHeader = styled.div`
  margin-bottom: 12px;
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  width: 100%;
`;

const AlertInfo = styled.div`
  flex: 1;
  text-align: left;
`;

const AlertTitleText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
`;

const AlertDate = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const AlertDetails = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 400;
  text-align: right;
`;

const StatusBadge = styled.span<{ $status: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => ($status ? '#dcfce7' : '#fee2e2')};
  color: ${({ $status }) => ($status ? '#16a34a' : '#dc2626')};
  
  &::before {
    content: '●';
    font-size: 8px;
  }
`;

// ============================================================================
// Component
// ============================================================================

/**
 * AlertHistory Component
 * 
 * Displays a list of alerts with expandable details.
 * Each alert card shows summary information and can be expanded to view full details.
 * 
 * @component
 * @example
 * ```tsx
 * <AlertHistory
 *   alerts={alertData}
 *   title="Alert History & Statistics"
 *   subtitle="View and manage your alert history"
 *   headerActions={<Button>Filter</Button>}
 * />
 * ```
 */
export const AlertHistory: React.FC<AlertHistoryProps> = ({
  alerts,
  title,
  subtitle,
  headerActions,
  className,
}) => {
  return (
    <Container className={className}>
      {(title || subtitle || headerActions) && (
        <HistoryHeader>
          {title && <HistoryTitle>{title}</HistoryTitle>}
          {subtitle && <HistorySubtitle>{subtitle}</HistorySubtitle>}
          {headerActions && (
            <HistoryHeaderActions>{headerActions}</HistoryHeaderActions>
          )}
        </HistoryHeader>
      )}
      
      <AlertList>
        {alerts?.length > 0 && (
          <ListHeader>
            <strong>Recent Alerts:</strong>
          </ListHeader>
        )}
        
        {alerts.map((alert) => {
          const accordionTitle = (
            <AccordionTitle>
              <AlertInfo>
                <AlertTitleText>{alert.name} - {alert.triggeredAt}</AlertTitleText>
                <AlertDate>Pattern: {alert.pattern}</AlertDate>
              </AlertInfo>
            </AccordionTitle>
          );
          
          return (
            <Accordion
              key={alert.id}
              title={accordionTitle}
              defaultCollapsed={true}
            >
              <AlertDetails>
                <DetailGrid>
                  <DetailSection>
                    <SectionTitle>Pattern: {alert.pattern}</SectionTitle>
                    <DetailRow>
                      <DetailLabel>Triggered At:</DetailLabel>
                      <DetailValue>{alert.triggeredAt}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Match Details:</DetailLabel>
                      <DetailValue>{alert.matchDetails}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Email Sent:</DetailLabel>
                      <DetailValue>
                        <StatusBadge $status={alert.emailSent}>
                          {alert.emailSent ? 'Yes' : 'No'}
                        </StatusBadge>
                      </DetailValue>
                    </DetailRow>
                  </DetailSection>
                  
                  <DetailSection>
                    <SectionTitle>Log Entry Details:</SectionTitle>
                    <DetailRow>
                      <DetailLabel>Timestamp:</DetailLabel>
                      <DetailValue>{alert.logEntry.timestamp}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Endpoint:</DetailLabel>
                      <DetailValue>{alert.logEntry.endpoint}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Data Length:</DetailLabel>
                      <DetailValue>{alert.logEntry.dataLength}</DetailValue>
                    </DetailRow>
                  </DetailSection>
                </DetailGrid>
              </AlertDetails>
            </Accordion>
          );
        })}
        
        {alerts?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No alerts to display
          </div>
        )}
      </AlertList>
    </Container>
  );
};

export default AlertHistory;

